import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Button } from './ui/Primitives';
import { formatCallTime } from './CallHistoryItem';
import { redirectToCallSignIn } from '../utils/callLinks';
import './CallFollowUpPanel.css';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` });
const localInput = date => new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

export default function CallFollowUpPanel({ callId, onChange }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [offset, setOffset] = useState(0);
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [body, setBody] = useState('');
  const [reminderOpen, setReminderOpen] = useState(false);
  const [dueInput, setDueInput] = useState('');
  const requestId = useRef(null);
  const reminderVersion = useRef(0);
  const generation = useRef(0);
  const busyRef = useRef(false);
  const baseUrl = `${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${callId}`;

  const load = useCallback(async () => {
    const current = ++generation.current;
    try {
      const response = await axios.get(`${baseUrl}/follow-up`, { headers: authHeaders(), params: { offset } });
      if (current === generation.current) setData(response.data);
    } catch (failure) {
      if (current !== generation.current) return;
      // Do not retain private notes after access is revoked.
      if ([401, 403, 404].includes(failure.response?.status)) setData(null);
      if (failure.response?.status === 401) redirectToCallSignIn();
      setError('Follow-up details could not be loaded.');
    }
  }, [baseUrl, offset]);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => { if (!busyRef.current) load(); }, 15000);
    return () => { generation.current += 1; window.clearInterval(timer); };
  }, [load]);

  async function saveNote(event) {
    event.preventDefault();
    if (busyRef.current || !body.trim()) return;
    busyRef.current = true;
    generation.current += 1;
    setBusy('note');
    setError('');
    try {
      if (!requestId.current) requestId.current = window.crypto.randomUUID();
      await axios.post(`${baseUrl}/notes`, { body: body.trim(), requestId: requestId.current }, { headers: authHeaders() });
      requestId.current = null;
      setBody('');
      setNoteOpen(false);
      setNotesOpen(true);
      setOffset(0);
      await load();
    } catch (failure) {
      setError(failure.response?.data?.error?.message || 'Note could not be saved. Retry to confirm it was added.');
      if ([401, 403, 404].includes(failure.response?.status)) setData(null);
    } finally { busyRef.current = false; setBusy(''); }
  }

  function editReminder() {
    reminderVersion.current = data.reminderVersion;
    setDueInput(localInput(data.reminderDueAt ? new Date(data.reminderDueAt) : new Date(Date.now() + 3600000)));
    setReminderOpen(true);
    setError('');
  }

  async function saveReminder(event, clear = false) {
    event.preventDefault();
    if (busyRef.current || !data) return;
    const due = clear ? null : new Date(dueInput);
    if (!clear && (!dueInput || Number.isNaN(due.getTime()) || due.getTime() <= Date.now() || localInput(due) !== dueInput)) {
      setError('Choose a valid future reminder time.');
      return;
    }
    busyRef.current = true;
    generation.current += 1;
    setBusy('reminder');
    setError('');
    try {
      const response = await axios.put(`${baseUrl}/reminder`, {
        dueAt: due ? due.toISOString() : null,
        version: clear ? data.reminderVersion : reminderVersion.current,
      }, { headers: authHeaders() });
      setData(current => current ? { ...current, ...response.data } : current);
      setReminderOpen(false);
      if (onChange) onChange();
    } catch (failure) {
      setError(failure.response?.data?.error?.message || 'Reminder could not be updated.');
      if (failure.response?.status === 409) setReminderOpen(false);
      await load();
    } finally { busyRef.current = false; setBusy(''); }
  }

  const number = typeof data?.callbackNumber === 'string' && /^\+[1-9][0-9]{7,14}$/.test(data.callbackNumber) ? data.callbackNumber : null;
  const isDue = data?.reminderDueAt && new Date(data.reminderDueAt).getTime() <= Date.now();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return <section className="call-follow-up" aria-label="Call follow-up">
    <div className="call-follow-up-actions">
      {number ? <Button size="sm" variant="secondary" href={`tel:${number}`} title={`Call ${number}`}>Call back <span className="call-follow-up-number">{number}</span></Button>
        : <span title="No confirmed callback number was recorded for this call."><Button size="sm" variant="secondary" disabled aria-label="Call back unavailable: no confirmed callback number recorded">Call back</Button></span>}
      <Button size="sm" variant="secondary" disabled={!data || Boolean(busy)} onClick={editReminder}>{data?.reminderDueAt ? 'Edit reminder' : 'Remind me'}</Button>
      <Button size="sm" variant="secondary" disabled={!data || Boolean(busy)} onClick={() => setNoteOpen(open => !open)} aria-expanded={noteOpen}>Add note</Button>
    </div>
    {data?.reminderDueAt ? <div className={`call-follow-up-reminder${isDue ? ' due' : ''}`}>
      <span>{isDue ? 'Reminder due' : 'Your reminder'} · {formatCallTime(data.reminderDueAt)}</span>
      <button type="button" disabled={Boolean(busy)} onClick={event => saveReminder(event, true)}>Clear</button>
    </div> : null}
    {reminderOpen && data ? <form className="call-follow-up-form" onSubmit={saveReminder}>
      <label>Remind me <small>Only you · {timeZone}</small><input type="datetime-local" required value={dueInput} onChange={event => setDueInput(event.target.value)} disabled={Boolean(busy)} /></label>
      <div><Button size="sm" type="submit" loading={busy === 'reminder'} disabled={Boolean(busy)}>Save reminder</Button><Button size="sm" variant="secondary" disabled={Boolean(busy)} onClick={() => setReminderOpen(false)}>Cancel</Button></div>
    </form> : null}
    {noteOpen && data ? <form className="call-follow-up-form" onSubmit={saveNote}>
      <label>Internal note <small>Visible to teammates who can access this call</small><textarea rows={3} maxLength={2000} required value={body} disabled={Boolean(busy)} onChange={event => {
        setBody(event.target.value); requestId.current = null;
      }} placeholder="What happened when you followed up?" /></label>
      <div><Button size="sm" type="submit" loading={busy === 'note'} disabled={Boolean(busy) || !body.trim()}>Save note</Button><Button size="sm" variant="secondary" disabled={Boolean(busy)} onClick={() => setNoteOpen(false)}>Cancel</Button></div>
    </form> : null}
    {error ? <Alert tone="warning">{error} {!data ? <button type="button" onClick={() => { setError(''); load(); }}>Retry</button> : null}</Alert> : null}
    {data?.notesTotal ? <div className="call-follow-up-notes">
      <button className="call-follow-up-notes-toggle" type="button" aria-expanded={notesOpen} onClick={() => setNotesOpen(open => !open)}>Internal notes ({data.notesTotal}) <span aria-hidden="true">{notesOpen ? '▴' : '▾'}</span></button>
      {notesOpen ? <>
        {data.notes.map(note => <article key={note.id}><div><strong>{note.authorName}</strong><time dateTime={note.createdAt}>{formatCallTime(note.createdAt)}</time></div><p>{note.body}</p></article>)}
        {offset > 0 || offset + data.notes.length < data.notesTotal ? <div className="call-follow-up-note-pages"><Button size="sm" variant="secondary" disabled={Boolean(busy) || offset === 0} onClick={() => setOffset(value => Math.max(value - 50, 0))}>Newer notes</Button><Button size="sm" variant="secondary" disabled={Boolean(busy) || offset + data.notes.length >= data.notesTotal} onClick={() => setOffset(value => value + 50)}>Older notes</Button></div> : null}
      </> : null}
    </div> : null}
  </section>;
}
